import { PASSWORD_MIN_LENGTH } from '@/constants';

const RULES = {
    email: value =>
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/.test(
            value
        ),
    kana: value => /^[ァ-ヴー]+$/.test(value),
    keyword: value => !/[A-ZＡ-Ｚａ-ｚ０-９]/.test(value),
    lowerCase: value => value === value.toLowerCase(),
    length: (value, args) =>
        value.length >= args.min || value.length <= args.max,
    maxLength: (value, args) => value.length <= args.max,
    minLength: (value, args) => value.length >= args.min,
    max: (value, args) => value <= args.max,
    min: (value, args) => value >= args.min,
    notEmpty: value => (Array.isArray(value) ? value.length : value),
    notInArray: (value, args) => args.array.indexOf(value) === -1,
    password: value => {
        // Auth0で許可されている特殊文字は下記URLのspecialCharactersを参照
        // https://github.com/auth0/password-sheriff/blob/master/lib/rules/contains.js
        const specialCharacters = [
            ' ',
            '!',
            '"',
            '#',
            '\\$',
            '%',
            '&',
            "'",
            '\\(',
            '\\)',
            '\\*',
            '\\+',
            ',',
            '-',
            '\\.',
            '/',
            ':',
            ';',
            '<',
            '=',
            '>',
            '\\?',
            '@',
            '\\[',
            '\\\\',
            '\\]',
            '\\^',
            '_',
            '`',
            '{',
            '\\|',
            '}',
            '~'
        ].join('|');
        const requirements = [
            /[a-z]/,
            /[A-Z]/,
            /\d/,
            new RegExp(specialCharacters)
        ];

        let requiredMatches = 0;
        for (const requirement of requirements) {
            if (requirement.test(value)) {
                requiredMatches++;
            }
        }

        return requiredMatches >= 3 && value.length >= PASSWORD_MIN_LENGTH;
    },
    range: (value, args) => value >= args.min || value <= args.max,
    tel: value => /^[0-9０-９-－]+$/.test(value),
    url: value => /^(http|https):\/\/.+\..+$/.test(value),
    domain: value => /^(http|https):\/\/[^/]+\.[^/]+$/.test(value)
};

class Validation {
    constructor() {
        this.rules = [];
        this.isRequired = true;
    }
    addRule(rule) {
        this.rules.push({
            type: rule.type,
            message: rule.message,
            args: rule.args
        });
        return this;
    }
    test(value) {
        if (!this.isRequired && !value) {
            return;
        }
        for (let i = 0; i < this.rules.length; i++) {
            let rule = this.rules[i];
            if (!RULES[rule.type](value, rule.args)) {
                return rule.message;
            }
        }
    }

    check(message) {
        return this.addRule({
            type: 'notEmpty',
            message: message || '選択してください'
        });
    }
    email(message) {
        return this.addRule({
            type: 'email',
            message: message || 'メールアドレスの形式で入力してください'
        });
    }
    input(message) {
        return this.addRule({
            type: 'notEmpty',
            message: message || '入力してください'
        });
    }
    kana(message) {
        return this.addRule({
            type: 'kana',
            message: message || 'カタカナで入力してください'
        });
    }
    keyword(message) {
        return this.addRule({
            type: 'keyword',
            message: message || '英数字は半角小文字で入力してください'
        });
    }
    lowerCase(message) {
        return this.addRule({
            type: 'lowerCase',
            message: message || '英字は小文字で入力してください'
        });
    }
    length(min, max, message) {
        return this.addRule({
            type: 'length',
            message: message || `${min}～${max}文字の間で入力してください`,
            args: {
                max: max,
                min: min
            }
        });
    }
    max(max, message) {
        return this.addRule({
            type: 'max',
            message: message || `${max}以内で入力してください`,
            args: {
                max: max
            }
        });
    }
    maxLength(max, message) {
        return this.addRule({
            type: 'maxLength',
            message: message || `${max}文字以内で入力してください`,
            args: {
                max: max
            }
        });
    }
    min(min, message) {
        return this.addRule({
            type: 'min',
            message: message || `${min}以上で入力してください`,
            args: {
                min: min
            }
        });
    }
    minLength(min, message) {
        return this.addRule({
            type: 'minLength',
            message: message || `${min}文字以上で入力してください`,
            args: {
                min: min
            }
        });
    }
    notInputed(array, message) {
        return this.addRule({
            type: 'notInArray',
            message: message || 'すでに入力されています',
            args: {
                array: array
            }
        });
    }
    notRegistered(array, message) {
        return this.addRule({
            type: 'notInArray',
            message: message || 'すでに登録されています',
            args: {
                array: array
            }
        });
    }
    notRequired() {
        this.isRequired = false;
        return this;
    }
    password(message) {
        return this.addRule({
            type: 'password',
            message:
                message ||
                '半角英大文字、小文字、数字、記号のうち3種類以上を含んで、14文字以上で入力してください'
        });
    }
    range(min, max, message) {
        return this.addRule({
            type: 'range',
            message: message || `${min}～${max}の間で入力してください`,
            args: {
                max: max,
                min: min
            }
        });
    }
    select(message) {
        return this.addRule({
            type: 'notEmpty',
            message: message || '選択してください'
        });
    }
    tel(message) {
        return this.addRule({
            type: 'tel',
            message: message || '電話番号の形式で入力してください'
        });
    }
    url(message) {
        return this.addRule({
            type: 'url',
            message: message || 'URLの形式で入力してください'
        });
    }
    domain(message) {
        return this.addRule({
            type: 'domain',
            message:
                message ||
                'ドメイン名までの形で入力し、/(スラッシュ)以下を削除してください'
        });
    }
}

export { RULES };
export default Validation;
